// /signup-paciente/error — pantalla terminal del flow del paciente
// cuando el token está inválido, expirado o ya usado.
//
// El código del error viene en route state desde SignupPaciente.tsx.
// Si llega sin state (deep-link directo, refresh), default a
// TOKEN_INVALID.

import { Link, useLocation } from "react-router";
import { AlertCircle, Clock, CheckCircle2 } from "lucide-react";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/ui/button";

type ErrorCode = "TOKEN_INVALID" | "TOKEN_EXPIRED" | "TOKEN_USED";

interface ErrorVariant {
  title: string;
  subtitle: string;
  body: string;
  icon: typeof AlertCircle;
  iconColor: string;
  /** Si el flow termina con CTA a login (caso TOKEN_USED) en vez de
   *  CTA "Contactá a tu kine". */
  ctaToLogin?: boolean;
}

const VARIANTS: Record<ErrorCode, ErrorVariant> = {
  TOKEN_INVALID: {
    title: "Link inválido",
    subtitle: "Este link no es válido.",
    body: "Puede que esté mal copiado o que ya no exista. Pedile a tu kinesiólogo que te envíe un link nuevo.",
    icon: AlertCircle,
    iconColor: "text-red-600",
  },
  TOKEN_EXPIRED: {
    title: "Link expirado",
    subtitle: "Este link ya no es válido.",
    body: "Los links de invitación son válidos por 7 días. Pedile a tu kinesiólogo que te envíe uno nuevo.",
    icon: Clock,
    iconColor: "text-amber-600",
  },
  TOKEN_USED: {
    title: "Cuenta ya activada",
    subtitle: "Esta invitación ya fue usada.",
    body: "Si vos activaste la cuenta, iniciá sesión con tu email y contraseña. Si no fuiste vos, contactá a tu kinesiólogo.",
    icon: CheckCircle2,
    iconColor: "text-green-600",
    ctaToLogin: true,
  },
};

export function SignupPacienteError() {
  const location = useLocation();
  const state = location.state as { code?: string } | null;
  const code = (state?.code as ErrorCode) || "TOKEN_INVALID";
  const variant = VARIANTS[code] ?? VARIANTS.TOKEN_INVALID;
  const Icon = variant.icon;

  return (
    <AuthLayout title={variant.title} subtitle={variant.subtitle}>
      <div className="flex items-center justify-center py-4">
        <Icon className={`w-12 h-12 ${variant.iconColor}`} strokeWidth={1.5} />
      </div>
      <p className="text-sm text-muted-foreground text-center mb-6 leading-relaxed">
        {variant.body}
      </p>
      {variant.ctaToLogin ? (
        <Link to="/login" className="block">
          <Button type="button" className="w-full">
            Iniciar sesión
          </Button>
        </Link>
      ) : (
        <Link to="/login" className="block">
          <Button type="button" variant="outline" className="w-full">
            Volver al login
          </Button>
        </Link>
      )}
    </AuthLayout>
  );
}
